import * as React from "react";
import {
  ArrowLeft, Search, Loader2, ChevronRight, ChevronDown, HelpCircle,
  ArrowUpDown, AlertTriangle, Layers, BarChart3
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Treemap, ResponsiveContainer, Tooltip as RTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
  PieChart, Pie
} from "recharts";
import axios from "axios";
import { toast } from "sonner";
import { trackActivity } from "@/utils/trackActivity";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const HelpTip = ({ text, side = "top" }) => (
  <Tooltip delayDuration={200}>
    <TooltipTrigger asChild><HelpCircle className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-help inline-block ml-1" /></TooltipTrigger>
    <TooltipContent side={side} className="max-w-xs text-xs leading-relaxed bg-slate-900 text-slate-100 px-3 py-2">{text}</TooltipContent>
  </Tooltip>
);
const fmt = (n) => n != null ? Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "0";
const fmtM = (n) => { if (!n) return "$0"; if (n >= 1e6) return "$"+(n/1e6).toFixed(1)+"M"; if (n >= 1e3) return "$"+(n/1e3).toFixed(1)+"K"; return "$"+n.toFixed(0); };
const OBJ_S = { awareness:{c:"bg-purple-100 text-purple-700",l:"Awareness"}, consideration:{c:"bg-blue-100 text-blue-700",l:"Consideration"}, conversion:{c:"bg-emerald-100 text-emerald-700",l:"Conversion"}, unclassified:{c:"bg-slate-100 text-slate-500",l:"N/C"} };
const PLAT_C = { google_ads:"bg-blue-100 text-blue-700", facebook_ads:"bg-indigo-100 text-indigo-700", tiktok_ads:"bg-slate-800 text-white", dv360:"bg-green-100 text-green-700" };

// === CLIENT LIST ===
const ClientList = ({ onSelect, days, setDays }) => {
  const [clients, setClients] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  React.useEffect(() => {
    (async () => { setIsLoading(true); try { const r = await axios.get(`${API}/campaign-structure/clients?days=${days}`, { withCredentials: true }); setClients(r.data.clients||[]); trackActivity("view_module", "campaign_structure"); } catch(e) { toast.error("Error"); } finally { setIsLoading(false); } })();
  }, [days]);
  const filtered = clients.filter(c => (c.cliente || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Estructura de Campanas</h2>
          <p className="text-slate-500 text-sm mt-1">{clients.length} clientes con actividad</p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 dias</SelectItem><SelectItem value="14">14 dias</SelectItem>
            <SelectItem value="30">30 dias</SelectItem><SelectItem value="60">60 dias</SelectItem>
            <SelectItem value="90">90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar cliente..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>
      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => (
            <Card key={c.cliente} className="cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5" onClick={() => { trackActivity("select_client", "campaign_structure", { cliente: c.cliente }); onSelect(c.cliente); }}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-slate-800">{c.cliente}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{fmtM(c.spend)} · {c.platforms} plataformas</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-300" />
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-center">
                  <div className="p-2 rounded bg-slate-50"><p className="text-lg font-bold text-slate-800">{c.campaigns}</p><p className="text-slate-400">Campanas</p></div>
                  <div className="p-2 rounded bg-slate-50"><p className="text-lg font-bold text-indigo-600">{c.total_ad_groups}</p><p className="text-slate-400">Ad Groups</p></div>
                  <div className="p-2 rounded bg-slate-50"><p className="text-lg font-bold text-slate-600">{c.avg_ag_per_camp}</p><p className="text-slate-400">AG/Camp</p></div>
                </div>
                {(c.bloated_camps > 0 || c.single_ag_camps > c.campaigns * 0.5) && (
                  <div className="mt-2 space-y-1">
                    {c.bloated_camps > 0 && <div className="text-[10px] text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{c.bloated_camps} campanas con +10 ad groups</div>}
                    {c.single_ag_camps > c.campaigns * 0.5 && <div className="text-[10px] text-slate-500 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{c.single_ag_camps} campanas con 1 solo ad group</div>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// === DASHBOARD ===
const Dashboard = ({ cliente, days, setDays, onBack }) => {
  const [data, setData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [expandedCamp, setExpandedCamp] = React.useState(null);
  const [adGroups, setAdGroups] = React.useState({});
  const [loadingAG, setLoadingAG] = React.useState(null);
  const [sortKey, setSortKey] = React.useState("spend");
  const [sortDir, setSortDir] = React.useState("desc");

  React.useEffect(() => {
    (async () => { setIsLoading(true); try { const r = await axios.get(`${API}/campaign-structure/dashboard/${encodeURIComponent(cliente)}?days=${days}`, { withCredentials: true }); setData(r.data); } catch(e) { toast.error("Error"); } finally { setIsLoading(false); } })();
  }, [cliente, days]);

  const loadAdGroups = async (campId) => {
    if (expandedCamp === campId) { setExpandedCamp(null); return; }
    setExpandedCamp(campId);
    if (adGroups[campId]) return;
    setLoadingAG(campId);
    try {
      const r = await axios.get(`${API}/campaign-structure/ad-groups/${campId}?days=${days}`, { withCredentials: true });
      setAdGroups(prev => ({ ...prev, [campId]: r.data.ad_groups }));
    } catch(e) { toast.error("Error al cargar ad groups"); }
    finally { setLoadingAG(null); }
  };

  if (isLoading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!data) return null;
  const { summary: s, campaigns } = data;

  const toggleSort = (k) => { if (sortKey===k) setSortDir(sortDir==="desc"?"asc":"desc"); else { setSortKey(k); setSortDir("desc"); } };
  const sorted = [...campaigns].sort((a,b) => sortDir==="desc" ? (b[sortKey]||0)-(a[sortKey]||0) : (a[sortKey]||0)-(b[sortKey]||0));
  const SortTH = ({ k, children, className="" }) => (
    <TableHead className={`cursor-pointer select-none hover:bg-slate-50 ${className}`} onClick={() => toggleSort(k)}>
      <span className="inline-flex items-center gap-0.5">{children}{sortKey===k && <ArrowUpDown className="h-3 w-3 text-indigo-500" />}</span>
    </TableHead>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Clientes</Button>
          <div className="h-5 w-px bg-slate-200" />
          <h2 className="text-xl font-bold text-slate-800">{cliente}</h2>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="7">7 dias</SelectItem><SelectItem value="14">14 dias</SelectItem><SelectItem value="30">30 dias</SelectItem><SelectItem value="60">60 dias</SelectItem><SelectItem value="90">90 dias</SelectItem></SelectContent>
        </Select>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: "Campanas", value: s.total_campaigns, tip: "Campanas activas con gasto en el periodo" },
          { label: "Ad Groups", value: s.total_ad_groups, tip: "Total de ad groups activos" },
          { label: "Prom. AG/Camp", value: s.avg_ag_per_camp, tip: "Promedio de ad groups por campana. Ideal: 3-7 para testing" },
          { label: "1 solo AG", value: s.single_ag, color: s.single_ag > s.total_campaigns*0.5 ? "text-amber-600" : "", tip: "Campanas con un solo ad group. Limita la capacidad de testing A/B" },
          { label: "+10 AG", value: s.bloated, color: s.bloated > 0 ? "text-red-600" : "", tip: "Campanas con mas de 10 ad groups. Puede diluir el presupuesto y dificultar la optimizacion" },
        ].map((kpi, i) => (
          <Card key={i}>
            <CardContent className="p-3">
              <p className="text-[11px] text-slate-400 inline-flex items-center">{kpi.label}<HelpTip text={kpi.tip} /></p>
              <p className={`text-lg font-bold mt-0.5 ${kpi.color || "text-slate-800"}`}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* 1. Donut: Spend by Objective */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Gasto por Objetivo
              <HelpTip text="Distribucion del presupuesto segun el objetivo inferido del nombre de campaña." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const objData = {};
              campaigns.forEach(c => {
                const obj = c.objective || "unclassified";
                if (!objData[obj]) objData[obj] = { name: (OBJ_S[obj]||OBJ_S.unclassified).l, value: 0, count: 0 };
                objData[obj].value += c.spend;
                objData[obj].count++;
              });
              const pieData = Object.values(objData).filter(d => d.value > 0);
              const COLORS = { Awareness: "#a855f7", Consideration: "#3b82f6", Conversion: "#10b981", "N/C": "#94a3b8" };
              const total = pieData.reduce((a, d) => a + d.value, 0);
              return (
                <div>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {pieData.map((d, i) => <Cell key={i} fill={COLORS[d.name] || "#94a3b8"} />)}
                      </Pie>
                      <RTooltip formatter={(v) => fmtM(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-2 justify-center mt-1">
                    {pieData.map(d => (
                      <span key={d.name} className="text-[10px] text-slate-500">
                        <span className="inline-block w-2 h-2 rounded-full mr-1" style={{ backgroundColor: COLORS[d.name] }} />
                        {d.name} {Math.round(d.value / total * 100)}%
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* 2. Bar: Ad Groups Distribution */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Ad Groups por Campana
              <HelpTip text="Cuantas campanas tienen 1, 2-3, 4-7, o mas de 8 ad groups. Ideal: 3-7 para A/B testing." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const buckets = [
                { name: "1 AG", count: 0, color: "#f59e0b" },
                { name: "2-3", count: 0, color: "#3b82f6" },
                { name: "4-7", count: 0, color: "#10b981" },
                { name: "8+", count: 0, color: "#ef4444" },
              ];
              campaigns.forEach(c => {
                if (c.ad_groups === 1) buckets[0].count++;
                else if (c.ad_groups <= 3) buckets[1].count++;
                else if (c.ad_groups <= 7) buckets[2].count++;
                else buckets[3].count++;
              });
              return (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={buckets} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={35} />
                    <RTooltip formatter={(v) => `${v} campanas`} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {buckets.map((b, i) => <Cell key={i} fill={b.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>

        {/* 3. Treemap: Spend by Campaign */}
        <Card>
          <CardHeader className="pb-1">
            <CardTitle className="text-sm inline-flex items-center gap-1">
              Mapa de Gasto
              <HelpTip text="Cada rectangulo es una campana. El tamano representa la proporcion del gasto. Identifica visualmente donde se concentra el presupuesto." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const OBJ_COLORS = { awareness: "#a855f7", consideration: "#3b82f6", conversion: "#10b981", unclassified: "#94a3b8" };
              const treeData = campaigns.slice(0, 30).map(c => ({
                name: c.campaign_name?.length > 25 ? c.campaign_name.substring(0, 25) + "..." : c.campaign_name,
                size: c.spend,
                fill: OBJ_COLORS[c.objective] || "#94a3b8"
              }));
              const CustomContent = (props) => {
                const { x, y, width, height, name, fill } = props;
                if (width < 30 || height < 20) return null;
                return (
                  <g>
                    <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" strokeWidth={2} rx={3} />
                    {width > 50 && height > 25 && (
                      <text x={x + 4} y={y + 14} fill="#fff" fontSize={9} fontWeight={500}>
                        {name?.substring(0, Math.floor(width / 6))}
                      </text>
                    )}
                  </g>
                );
              };
              return (
                <ResponsiveContainer width="100%" height={200}>
                  <Treemap data={treeData} dataKey="size" nameKey="name" content={<CustomContent />}>
                    <RTooltip formatter={(v) => fmtM(v)} />
                  </Treemap>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Table with expandable ad groups */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm inline-flex items-center gap-1">
            Jerarquia de Campanas
            <HelpTip text="Haz clic en una fila para expandir y ver los ad groups de esa campana con su distribucion de gasto." />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-[600px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[3%] sticky top-0 bg-white"></TableHead>
                  <TableHead className="w-[30%] sticky top-0 bg-white">Campana</TableHead>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Obj.</TableHead>
                  <TableHead className="w-[7%] sticky top-0 bg-white">Plataf.</TableHead>
                  <SortTH k="ad_groups" className="text-center w-[7%] sticky top-0 bg-white">AGs</SortTH>
                  <SortTH k="spend" className="text-right w-[9%] sticky top-0 bg-white">Spend</SortTH>
                  <SortTH k="spend_share" className="text-right w-[7%] sticky top-0 bg-white">%</SortTH>
                  <SortTH k="roas" className="text-right w-[7%] sticky top-0 bg-white">ROAS</SortTH>
                  <SortTH k="conversions" className="text-right w-[7%] sticky top-0 bg-white">Conv.</SortTH>
                  <TableHead className="text-right w-[7%] sticky top-0 bg-white">Dias</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map(c => {
                  const isExpanded = expandedCamp === c.campaign_id;
                  const objS = OBJ_S[c.objective] || OBJ_S.unclassified;
                  const agData = adGroups[c.campaign_id];
                  return (
                    <React.Fragment key={c.campaign_id}>
                      <TableRow className="text-xs cursor-pointer hover:bg-slate-50" onClick={() => loadAdGroups(c.campaign_id)}>
                        <TableCell className="text-center">
                          {loadingAG === c.campaign_id ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" /> :
                            isExpanded ? <ChevronDown className="h-3.5 w-3.5 text-indigo-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                        </TableCell>
                        <TableCell><p className="font-medium text-slate-700 truncate max-w-[250px]">{c.campaign_name}</p></TableCell>
                        <TableCell><Badge className={`text-[9px] ${objS.c}`}>{objS.l}</Badge></TableCell>
                        <TableCell><Badge className={`text-[9px] ${PLAT_C[c.platform]||"bg-slate-100"}`}>{c.platform?.replace("_ads","")}</Badge></TableCell>
                        <TableCell className="text-center">
                          <span className={`font-bold ${c.ad_groups > 10 ? "text-red-600" : c.ad_groups === 1 ? "text-amber-600" : "text-slate-700"}`}>{c.ad_groups}</span>
                        </TableCell>
                        <TableCell className="text-right font-mono">{fmtM(c.spend)}</TableCell>
                        <TableCell className="text-right font-mono text-slate-400">{c.spend_share}%</TableCell>
                        <TableCell className={`text-right font-mono ${c.roas >= 3 ? "text-emerald-600" : c.roas >= 1 ? "text-amber-600" : c.roas > 0 ? "text-red-600" : "text-slate-400"}`}>{c.roas > 0 ? c.roas+"x" : "-"}</TableCell>
                        <TableCell className="text-right font-mono">{c.conversions > 0 ? fmt(c.conversions) : "-"}</TableCell>
                        <TableCell className="text-right text-slate-400">{c.active_days}d</TableCell>
                      </TableRow>
                      {/* Expanded ad groups */}
                      {isExpanded && agData && agData.map(ag => (
                        <TableRow key={ag.ad_group_id} className="text-[11px] bg-indigo-50/30">
                          <TableCell></TableCell>
                          <TableCell className="pl-8">
                            <span className="text-indigo-600">{ag.ad_group_name}</span>
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right font-mono">{fmtM(ag.spend)}</TableCell>
                          <TableCell className="text-right font-mono text-slate-400">{ag.spend_share}%</TableCell>
                          <TableCell className={`text-right font-mono ${ag.roas >= 3 ? "text-emerald-600" : ag.roas >= 1 ? "text-amber-600" : ag.roas > 0 ? "text-red-600" : "text-slate-400"}`}>{ag.roas > 0 ? ag.roas+"x" : "-"}</TableCell>
                          <TableCell className="text-right font-mono">{ag.conversions > 0 ? fmt(ag.conversions) : "-"}</TableCell>
                          <TableCell className="text-right text-slate-400">{ag.active_days}d</TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// === MAIN ===
export const CampaignStructureView = ({ onBack }) => {
  const [view, setView] = React.useState("clients");
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [days, setDays] = React.useState(30);
  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/20">
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" />Volver</Button>
            <div className="h-6 w-px bg-slate-200" />
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 inline-flex items-center gap-1">
                  Campaign Structure
                  <HelpTip text="Analiza la jerarquia de campanas y ad groups. Identifica campanas con estructura suboptima: demasiados o muy pocos ad groups." side="bottom" />
                </h1>
                <p className="text-xs text-slate-500">Jerarquia, ad groups y distribucion de gasto</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {view === "clients" && <ClientList onSelect={(c) => { setSelectedClient(c); setView("dashboard"); }} days={days} setDays={setDays} />}
        {view === "dashboard" && <Dashboard cliente={selectedClient} days={days} setDays={setDays} onBack={() => { setSelectedClient(null); setView("clients"); }} />}
      </main>
    </div>
    </TooltipProvider>
  );
};
